/**
 * @file Contains helpers function for account components.
 */

import { getOffset } from '../../utils'
import set from 'lodash/set'
import isObject from 'lodash/isObject'
import store from 'store2'
import { defineMessages } from 'react-intl'

export const messages = defineMessages({
  status: {
    id: 'booking-status',
    defaultMessage: 'Booking status',
  },
  paymentInfo: {
    id: 'payment-info',
    defaultMessage: 'Payment info',
  },
  paymentStatus: {
    id: 'payment-status',
    defaultMessage: 'Payment status',
  },
  bookingRef: {
    id: 'booking-ref',
    defaultMessage: 'Booking ref',
  },
  cancelBooking: {
    id: 'cancel-booking',
    defaultMessage: 'Cancel booking',
  },
  gis: {
    id: 'gis',
    defaultMessage: 'GIS',
  },
  guestInfo: {
    id: 'guest-information',
    defaultMessage: 'Guest information',
  },
  ok: {
    id: 'ok',
    defaultMessage: 'Ok',
  },
  missing: {
    id: 'missing',
    defaultMessage: 'Missing',
  },
  failed: {
    id: 'failed',
    defaultMessage: 'Failed',
  },
  clickToAdd: {
    id: 'click_to_add',
    defaultMessage: 'Click to add',
  },
})

export const statuses = {
  vacation: {
    inactive: [10, 90, 110, 130, 140],
    cancelled: [30, 40, 50, 60, 70, 80, 120],
    completed: [20, 100],
  },
  adventure: {
    inactive: [10],
    cancelled: [30, 40, 60],
    completed: [20, 50],
  },
  gis: {
    inactive: [20],
    cancelled: [30],
    completed: [10],
  },
}

/** @function getStatusColor
 * Assign the project to an employee.
 * @param {number} status - Status kind for defining.
 * @param {String in ['vacation', 'adventure', 'gis']} type - status type that match keys from status
 * @returns {string} colorClass - The className for styling
 */
export function getStatusColor(status, type) {
  let colorClass

  if(statuses[type].inactive.indexOf(status) !== -1) {
    colorClass = 'inactive'
  } else if(statuses[type].cancelled.indexOf(status) !== -1) {
    colorClass = 'cancelled'
  } else if(statuses[type].completed.indexOf(status) !== -1) {
    colorClass = 'completed'
  }

  return colorClass
}

export function scrollToFormTop(selector = '.main-content') {
  const form = document.querySelector(selector)
  setTimeout(() => window.scrollTo(0, getOffset(form).top), 150)
}

/** @function getDeeperPath
 * Find path of field.
 * @param {Object} object - object to iterate
 * @param {String} field - key path of which should function return
 * @returns {Array} - array with arrays of possible path
 */

function getDeeperPath(object, field) {
  return Object.entries(object).reduce((acum, [key, value]) => {
    if(key === field) {
      return acum.concat(key)
    }
    if(value && typeof value === 'object') {
      getDeeperPath(value, field).forEach((result) => acum.push([key, result]))
    }
    return acum
  }, [])
}

/** @function updateUserStore
 * Updates user object in localStorage,
 * @param {Object} data with new values  (form.values)
 * @param {Array} fields - array with fields that were modified
 */
const user = store('user')

export const updateUserStore = (data, fields) => {
  fields.forEach((field) => {
    const pathes = getDeeperPath(user, field)

    // getting new value;
    let value = data[field]

    if(field.endsWith('.value')) {
      const fieldModified = field.slice(0, -6) // -6 index of .
      value = data[fieldModified].value
    }

    // assign value to each field in user object
    pathes.forEach((path) => {
      // pathes like ['field1', ['field2', 'innerInnerField']]
      const pathString = Array.isArray(path) ? path.join('.') : path
      set(user, pathString || '', value)
    })
  })
  store('user', user)
}

/** @function getCardBrandIcon
 * @param {String} brand - string with CC brand, kind of 'Visa' or 'American Express'
 * @param {String} css class
 */

export const getCardBrandIcon = (brand) => {
  let brandTitle = brand.toLowerCase()

  if(brand === 'American Express') {
    brandTitle = brand.toLowerCase().split(' ').join('_')
  }

  return `pm-icon pm-icon-${brandTitle}`
}

/** @function getPolicyMessage
 * @param {Object} policy - object with kind of activity cancellation policy and title for it (ex. 24 hours)
 * @returns {Object} for FormattedMessage
 */

export const getPolicyMessage = (policy) => {
  const messages = defineMessages({
    noRefund: {
      id: 'activity.detail.cancelation.policy.noRefund',
      defaultMessage: 'No cancelations, refunds, or changes can be made',
    },
    refund: {
      id: 'activity.detail.cancelation.policy.refund',
      defaultMessage:
        'Full refunds will be issued for cancelations made at least {value} prior to the activity',
    },
  })

  if(!isObject(policy)) {
    return messages.noRefund
  }

  const getText = () => {
    if(policy.value === 10) {
      return messages.noRefund
    }
    return messages.refund
  }

  return { values: { value: policy.title }, ...getText() }
}
